import React, { useState, useEffect, useCallback } from 'react';
import {
  PlusIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  CpuChipIcon,
  ArrowPathIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { Bot } from '../types';
import { botsApi, handleApiError } from '../utils/api';

export function Bots() {
  const [bots, setBots] = useState<Bot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Add bot form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newBotToken, setNewBotToken] = useState('');
  const [newBotUsername, setNewBotUsername] = useState('');
  const [newBotMaxSessions, setNewBotMaxSessions] = useState(100);
  const [addingBot, setAddingBot] = useState(false);

  // Delete confirmation
  const [deletingBotId, setDeletingBotId] = useState<number | null>(null);

  // Token visibility
  const [visibleTokens, setVisibleTokens] = useState<Set<number>>(new Set());

  const loadBots = useCallback(async () => {
    try {
      const response = await botsApi.getAll();
      setBots(response.data);
      setError(null);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBots();
  }, [loadBots]);

  // Auto-dismiss success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleAddBot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBotToken.trim()) return;

    setAddingBot(true);
    try {
      await botsApi.create(
        newBotToken.trim(),
        newBotUsername.trim() || undefined,
        newBotMaxSessions,
      );
      setNewBotToken('');
      setNewBotUsername('');
      setNewBotMaxSessions(100);
      setShowAddForm(false);
      setSuccessMessage('Bot added successfully! It will be auto-discovered by the bot manager.');
      await loadBots();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setAddingBot(false);
    }
  };

  const handleToggleActive = async (bot: Bot) => {
    try {
      await botsApi.updateActive(bot.id, !bot.is_active);
      setSuccessMessage(`Bot @${bot.bot_username || bot.id} ${bot.is_active ? 'deactivated' : 'activated'}`);
      await loadBots();
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  const handleToggleAvailability = async (bot: Bot) => {
    try {
      await botsApi.updateAvailability(bot.id, !bot.is_available);
      setSuccessMessage(`Bot @${bot.bot_username || bot.id} ${bot.is_available ? 'paused' : 'resumed'}`);
      await loadBots();
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  const handleDelete = async (bot: Bot) => {
    try {
      await botsApi.delete(bot.id);
      setDeletingBotId(null);
      setSuccessMessage(`Bot @${bot.bot_username || bot.id} deleted`);
      await loadBots();
    } catch (err) {
      setError(handleApiError(err));
      setDeletingBotId(null);
    }
  };

  const toggleTokenVisibility = (botId: number) => {
    setVisibleTokens((prev) => {
      const next = new Set(prev);
      if (next.has(botId)) {
        next.delete(botId);
      } else {
        next.add(botId);
      }
      return next;
    });
  };

  const maskToken = (token: string) => {
    if (token.length <= 10) return '••••••••••';
    return token.slice(0, 6) + '••••••' + token.slice(-4);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Bots</h1>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow loading-shimmer h-24" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bots</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage Telegram bots available for session creation. New bots are auto-discovered by the bot manager.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadBots}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowPathIcon className="h-4 w-4 mr-1" />
            Refresh
          </button>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Bot
          </button>
        </div>
      </div>

      {/* Success message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <CheckCircleIcon className="h-5 w-5 text-green-400" />
            <p className="ml-3 text-sm text-green-700">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
              <button
                onClick={() => setError(null)}
                className="mt-1 text-xs text-red-500 underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Bot Form */}
      {showAddForm && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Add New Bot</h2>
          <form onSubmit={handleAddBot} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bot Token <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newBotToken}
                  onChange={(e) => setNewBotToken(e.target.value)}
                  placeholder="123456789:ABCdefGHIjklMNOpqrSTUvwxYZ"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  required
                />
                <p className="mt-1 text-xs text-gray-400">Get this from @BotFather on Telegram</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bot Username
                </label>
                <input
                  type="text"
                  value={newBotUsername}
                  onChange={(e) => setNewBotUsername(e.target.value)}
                  placeholder="@my_bot"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
            </div>
            <div className="max-w-xs">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Sessions
              </label>
              <input
                type="number"
                value={newBotMaxSessions}
                onChange={(e) => setNewBotMaxSessions(Number(e.target.value))}
                min={1}
                max={10000}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={addingBot || !newBotToken.trim()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addingBot ? 'Adding...' : 'Add Bot'}
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm font-medium text-gray-500">Total Bots</div>
          <div className="mt-1 text-2xl font-semibold text-gray-900">{bots.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm font-medium text-gray-500">Active</div>
          <div className="mt-1 text-2xl font-semibold text-green-600">
            {bots.filter((b) => b.is_active).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm font-medium text-gray-500">Available for Sessions</div>
          <div className="mt-1 text-2xl font-semibold text-indigo-600">
            {bots.filter((b) => b.is_active && b.is_available).length}
          </div>
        </div>
      </div>

      {/* Bots Table */}
      {bots.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <CpuChipIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No bots</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by adding a Telegram bot.
          </p>
          <button
            onClick={() => setShowAddForm(true)}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Bot
          </button>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bot
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Token
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sessions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Container
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {bots.map((bot) => (
                <tr key={bot.id} className={!bot.is_active ? 'bg-gray-50 opacity-60' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <CpuChipIcon className={`h-8 w-8 mr-3 ${bot.is_active ? 'text-indigo-500' : 'text-gray-400'}`} />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {bot.bot_username ? `@${bot.bot_username}` : `Bot #${bot.id}`}
                        </div>
                        <div className="text-xs text-gray-500">ID: {bot.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <code className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {visibleTokens.has(bot.id) ? bot.bot_token : maskToken(bot.bot_token)}
                      </code>
                      <button
                        onClick={() => toggleTokenVisibility(bot.id)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title={visibleTokens.has(bot.id) ? 'Hide token' : 'Show token'}
                      >
                        {visibleTokens.has(bot.id) ? (
                          <EyeSlashIcon className="h-4 w-4" />
                        ) : (
                          <EyeIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          bot.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {bot.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {bot.is_active && (
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            bot.is_available
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {bot.is_available ? 'Available' : 'Paused'}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {bot.current_sessions} / {bot.max_sessions}
                    </div>
                    <div className="w-24 bg-gray-200 rounded-full h-1.5 mt-1">
                      <div
                        className="bg-indigo-600 h-1.5 rounded-full"
                        style={{
                          width: `${Math.min(100, (bot.current_sessions / bot.max_sessions) * 100)}%`,
                        }}
                      />
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {bot.container_hostname ? (
                      <span className="text-xs text-gray-500 font-mono">
                        {bot.container_hostname}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400 italic">Not assigned</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <div className="flex items-center justify-end gap-2">
                      {/* Toggle active */}
                      <button
                        onClick={() => handleToggleActive(bot)}
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          bot.is_active
                            ? 'text-red-700 bg-red-50 hover:bg-red-100'
                            : 'text-green-700 bg-green-50 hover:bg-green-100'
                        }`}
                        title={bot.is_active ? 'Deactivate bot' : 'Activate bot'}
                      >
                        {bot.is_active ? (
                          <XCircleIcon className="h-4 w-4" />
                        ) : (
                          <CheckCircleIcon className="h-4 w-4" />
                        )}
                      </button>

                      {/* Toggle availability */}
                      {bot.is_active && (
                        <button
                          onClick={() => handleToggleAvailability(bot)}
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            bot.is_available
                              ? 'text-yellow-700 bg-yellow-50 hover:bg-yellow-100'
                              : 'text-blue-700 bg-blue-50 hover:bg-blue-100'
                          }`}
                          title={bot.is_available ? 'Pause bot' : 'Resume bot'}
                        >
                          {bot.is_available ? 'Pause' : 'Resume'}
                        </button>
                      )}

                      {/* Delete */}
                      {deletingBotId === bot.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(bot)}
                            className="px-2 py-1 rounded text-xs font-medium text-white bg-red-600 hover:bg-red-700"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setDeletingBotId(null)}
                            className="px-2 py-1 rounded text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeletingBotId(bot.id)}
                          className="px-2 py-1 rounded text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100"
                          title="Delete bot"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
